import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const MOBILE_MENU_BREAKPOINT = '(max-width: 900px)';

const MobileMenuContext = createContext(null);

export const MobileMenuProvider = ({ children }) => {
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== 'undefined' ? window.matchMedia(MOBILE_MENU_BREAKPOINT).matches : false
    ));
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => (
        typeof window !== 'undefined' ? window.matchMedia(MOBILE_MENU_BREAKPOINT).matches : false
    ));

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_MENU_BREAKPOINT);
        let frameId = null;

        const applyViewportState = (matches) => {
            setIsMobile(matches);
            setIsMenuCollapsed(matches);
        };

        const handleViewportChange = (event) => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }

            frameId = window.requestAnimationFrame(() => {
                frameId = null;
                applyViewportState(event.matches);
            });
        };

        applyViewportState(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleViewportChange);

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
            mediaQuery.removeEventListener('change', handleViewportChange);
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            setIsMenuCollapsed(true);
        }
    }, [location.pathname, isMobile]);

    const openMenu = useCallback(() => setIsMenuCollapsed(false), []);
    const closeMenu = useCallback(() => setIsMenuCollapsed(true), []);
    const toggleMenu = useCallback(() => setIsMenuCollapsed((current) => !current), []);

    const value = useMemo(() => ({
        isMobile,
        isMenuCollapsed,
        isMobileMenuOpen: isMobile && !isMenuCollapsed,
        showMenuLabels: !isMenuCollapsed,
        setIsMenuCollapsed,
        openMenu,
        closeMenu,
        toggleMenu
    }), [isMobile, isMenuCollapsed, openMenu, closeMenu, toggleMenu]);

    return (
        <MobileMenuContext.Provider value={value}>
            {children}
        </MobileMenuContext.Provider>
    );
};

export const useMobileMenu = () => {
    const context = useContext(MobileMenuContext);
    if (!context) {
        throw new Error('useMobileMenu must be used within a MobileMenuProvider');
    }
    return context;
};
